// Math triggers mapped from lua/luasnips/tex_modules/math.lua
// Each trigger corresponds exactly to a snippet from your Neovim config

import type { TriggerDefinition } from "./types";

// ============================================================
// MATH FONT TRIGGERS (mc, mr, mb, ms, mt, mn, mi, mf, mk)
// ============================================================
const mathFontTriggers: TriggerDefinition[] = [
    { id: "tt", trigger: "tt", name: "Text", category: "math-font", latex: "\\text{#0}", aliases: ["text", "textmode", "upright"] },
    { id: "mc", trigger: "mc", name: "Calligraphic", category: "math-font", latex: "\\mathcal{#0}", aliases: ["calligraphic", "mathcal", "script"] },
    { id: "mr", trigger: "mr", name: "Roman", category: "math-font", latex: "\\mathrm{#0}", aliases: ["roman", "mathrm", "upright"] },
    { id: "mb", trigger: "mb", name: "Bold", category: "math-font", latex: "\\mathbf{#0}", aliases: ["bold", "mathbf", "boldface"] },
    { id: "ms", trigger: "ms", name: "Sans serif", category: "math-font", latex: "\\mathsf{#0}", aliases: ["sans", "mathsf", "sansserif"] },
    { id: "mt", trigger: "mt", name: "Typewriter", category: "math-font", latex: "\\mathtt{#0}", aliases: ["typewriter", "mathtt", "monospace"] },
    { id: "mn", trigger: "mn", name: "Normal", category: "math-font", latex: "\\mathnormal{#0}", aliases: ["normal", "mathnormal"] },
    { id: "mi", trigger: "mi", name: "Italic", category: "math-font", latex: "\\mathit{#0}", aliases: ["italic", "mathit"] },
    { id: "mf", trigger: "mf", name: "Fraktur", category: "math-font", latex: "\\mathfrak{#0}", aliases: ["fraktur", "mathfrak", "gothic"] },
    { id: "mk", trigger: "mk", name: "Blackboard bold", category: "math-font", latex: "\\mathbb{#0}", aliases: ["blackboard", "mathbb", "doublestruck"] },
];

// ============================================================
// FRACTION & BINOMIAL TRIGGERS
// ============================================================
const fractionTriggers: TriggerDefinition[] = [
    {
        id: "ff",
        trigger: "ff",
        name: "Fraction",
        category: "fraction",
        latex: "\\frac{#0}{#1}",
        choices: ["\\frac{#0}{#1}", "\\dfrac{#0}{#1}", "\\tfrac{#0}{#1}"],
        aliases: ["fraction", "frac", "divide"],
    },
    {
        id: "frac-slash",
        trigger: "//",
        name: "Fraction (//)",
        category: "fraction",
        latex: "\\frac{#0}{#1}",
    },
    {
        id: "cf",
        trigger: "cf",
        name: "Continued fraction",
        category: "fraction",
        latex: "\\cfrac{#0}{#1}",
        aliases: ["cfrac", "continued"],
    },
    {
        id: "div",
        trigger: "div",
        name: "Division (slash)",
        category: "fraction",
        latex: "#0/#1",
        aliases: ["slash", "solidus"],
    },
    {
        id: "bm",
        trigger: "bm",
        name: "Binomial",
        category: "fraction",
        latex: "\\binom{#0}{#1}",
        choices: ["\\binom{#0}{#1}", "\\dbinom{#0}{#1}", "\\tbinom{#0}{#1}"],
        aliases: ["binomial", "binom", "choose"],
    },
];

// ============================================================
// SUBSCRIPT & SUPERSCRIPT TRIGGERS
// ============================================================
const subSupTriggers: TriggerDefinition[] = [
    { id: "sub", trigger: ";", name: "Subscript", category: "subscript", latex: "_{#0}", wordTrig: false, aliases: ["subscript"] },
    { id: "sup", trigger: ":", name: "Superscript", category: "superscript", latex: "^{#0}", wordTrig: false, aliases: ["superscript", "power"] },
    { id: "bs", trigger: "bs", name: "Both sub & super", category: "subscript", latex: "_{#0}^{#1}", wordTrig: false, aliases: ["subsup", "bothscript"] },
    { id: "st", trigger: "st", name: "Substack", category: "subscript", latex: "\\substack{#0 \\\\ #1}", aliases: ["substack"] },
    { id: "pw2", trigger: "sr", name: "Squared", category: "superscript", latex: "^2", aliases: ["squared"] },
    { id: "pw3", trigger: "cb", name: "Cubed", category: "superscript", latex: "^3", aliases: ["cubed"] },
    { id: "compl", trigger: "compl", name: "Complement", category: "superscript", latex: "^{c}", aliases: ["complement"] },
    { id: "td", trigger: "td", name: "To power", category: "superscript", latex: "^{#0}", aliases: ["topower", "exponent"] },
];

// ============================================================
// DELIMITER TRIGGERS
// ============================================================
const delimiterTriggers: TriggerDefinition[] = [
    { id: "dp", trigger: "dp", name: "Parentheses", category: "delimiter", latex: "\\left( #0 \\right)", aliases: ["parentheses", "parens"] },
    { id: "ds", trigger: "ds", name: "Brackets", category: "delimiter", latex: "\\left[ #0 \\right]", aliases: ["brackets", "squarebrackets"] },
    { id: "bb", trigger: "bb", name: "Braces", category: "delimiter", latex: "\\{ #0 \\}", aliases: ["braces", "curly"] },
    { id: "db", trigger: "db", name: "Extensible braces", category: "delimiter", latex: "\\left\\{ #0 \\right\\}", aliases: ["bigbraces", "autobraces"] },
    {
        id: "dk",
        trigger: "dk",
        name: "Angle brackets",
        category: "delimiter",
        latex: "\\left\\langle #0 \\right\\rangle",
        choices: ["\\left\\langle #0 \\right\\rangle", "\\langle #0 \\rangle"],
        aliases: ["angle", "chevrons", "anglebrackets"],
    },
    {
        id: "da",
        trigger: "da",
        name: "Pipes",
        category: "delimiter",
        latex: "\\left\\lvert #0 \\right\\rvert",
        choices: ["\\left\\lvert #0 \\right\\rvert", "\\lvert #0 \\rvert"],
        aliases: ["absolute", "abs", "pipes"],
    },
    {
        id: "dn",
        trigger: "dn",
        name: "Double pipes",
        category: "delimiter",
        latex: "\\left\\lVert #0 \\right\\rVert",
        choices: ["\\left\\lVert #0 \\right\\rVert", "\\lVert #0 \\rVert"],
        aliases: ["norm", "magnitude", "doublepipes"],
    },
];

// ============================================================
// MATRIX TRIGGERS (regex-based)
// ============================================================
const matrixDelimMap: Record<string, string> = {
    b: "bmatrix",
    B: "Bmatrix",
    p: "pmatrix",
    v: "vmatrix",
    V: "Vmatrix",
};

function generateMatrix(type: string, rows: number, cols: number): string {
    const env = matrixDelimMap[type] || "pmatrix";
    let body = "";
    for (let r = 0; r < rows; r++) {
        const cells: string[] = [];
        for (let c = 0; c < cols; c++) {
            cells.push(`#${r * cols + c}`);
        }
        body += cells.join(" & ");
        if (r < rows - 1) body += " \\\\ ";
    }
    return `\\begin{${env}} ${body} \\end{${env}}`;
}

function generateCases(count: number): string {
    let body = "";
    const conditionLabels = ["if ", "else if ", "else"];
    for (let r = 0; r < count; r++) {
        const cond = conditionLabels[r] ?? "else";
        body += `#${r * 2} & \\text{${cond}#${r * 2 + 1}}`;
        if (r < count - 1) body += " \\\\ ";
    }
    return `\\begin{cases} ${body} \\end{cases}`;
}

// Homogeneous matrix: all entries use the same variable with subscripts (a_{11}, a_{12}, etc.)
function generateHomMatrix(type: string, rows: number, cols: number): string {
    const env = matrixDelimMap[type] || "pmatrix";
    let body = "";
    for (let r = 0; r < rows; r++) {
        const cells: string[] = [];
        for (let c = 0; c < cols; c++) {
            cells.push(`#0_{${r + 1}${c + 1}}`);
        }
        body += cells.join(" & ");
        if (r < rows - 1) body += " \\\\ ";
    }
    return `\\begin{${env}} ${body} \\end{${env}}`;
}

// Generic matrix - firstCols specifies how many first columns to show,
// firstRows specifies how many first rows to show before the vdots row.
// withSubscripts: true = entries have subscripts (a_{11}), false = plain placeholders
function generateGenericMatrix(type: string, firstCols: number = 2, withSubscripts: boolean = true, firstRows: number = 2): string {
    const env = matrixDelimMap[type] || "pmatrix";

    if (withSubscripts) {
        const rows: string[] = [];

        // Build explicit rows: a_{r1} & a_{r2} & ... & a_{r,firstCols} & \cdots & a_{rn}
        for (let r = 1; r <= firstRows; r++) {
            let row = "";
            for (let c = 1; c <= firstCols; c++) {
                row += `#0_{${r}${c}}`;
                row += " & ";
            }
            row += `\\cdots & #0_{${r}#1}`;
            rows.push(row);
        }

        // Build vdots row
        let vdotsRow = "";
        for (let c = 1; c <= firstCols; c++) {
            vdotsRow += "\\vdots & ";
        }
        vdotsRow += "\\ddots & \\vdots";
        rows.push(vdotsRow);

        // Build last row: a_{m1} & a_{m2} & ... & a_{m,firstCols} & \cdots & a_{mn}
        let lastRow = "";
        for (let c = 1; c <= firstCols; c++) {
            lastRow += `#0_{#2${c}}`;
            lastRow += " & ";
        }
        lastRow += `\\cdots & #0_{#2#1}`;
        rows.push(lastRow);

        return `\\begin{${env}} ${rows.join(" \\\\ ")} \\end{${env}}`;
    } else {
        const rows: string[] = [];
        let placeholderIdx = 0;

        // Build explicit rows with plain placeholders
        for (let r = 0; r < firstRows; r++) {
            let row = "";
            for (let c = 0; c < firstCols; c++) {
                row += `#${placeholderIdx++}`;
                row += " & ";
            }
            row += `\\cdots & #${placeholderIdx++}`;
            rows.push(row);
        }

        // Build vdots row
        let vdotsRow = "";
        for (let c = 1; c <= firstCols; c++) {
            vdotsRow += "\\vdots & ";
        }
        vdotsRow += "\\ddots & \\vdots";
        rows.push(vdotsRow);

        // Build last row
        let lastRow = "";
        for (let c = 0; c < firstCols; c++) {
            lastRow += `#${placeholderIdx++}`;
            lastRow += " & ";
        }
        lastRow += `\\cdots & #${placeholderIdx++}`;
        rows.push(lastRow);

        return `\\begin{${env}} ${rows.join(" \\\\ ")} \\end{${env}}`;
    }
}

// Dotted matrix with individual placeholders
function generateDottedMatrix(type: string): string {
    const env = matrixDelimMap[type] || "pmatrix";
    return `\\begin{${env}} #0 & #1 & \\cdots & #2 \\\\ #3 & #4 & \\cdots & #5 \\\\ \\vdots & \\vdots & \\ddots & \\vdots \\\\ #6 & #7 & \\cdots & #8 \\end{${env}}`;
}

// General row vector
function generateRowVector(type: string): string {
    const env = matrixDelimMap[type] || "pmatrix";
    return `\\begin{${env}} #0 & #1 & \\cdots & #2 \\end{${env}}`;
}

// General column vector
function generateColVector(type: string): string {
    const env = matrixDelimMap[type] || "pmatrix";
    return `\\begin{${env}} #0 \\\\ #1 \\\\ \\vdots \\\\ #2 \\end{${env}}`;
}

const matrixTriggers: TriggerDefinition[] = [
    {
        id: "matrix-nxm",
        trigger: "",
        name: "Matrix NxM",
        category: "matrix",
        latex: "",
        isRegex: true,
        regexPattern: "([bBpvV])(\\d+)x(\\d+)",
        dynamicGenerator: (caps) => generateMatrix(caps[0], parseInt(caps[1]), parseInt(caps[2])),
    },
    {
        id: "matrix-hom",
        trigger: "",
        name: "Homogeneous Matrix",
        category: "matrix",
        latex: "",
        isRegex: true,
        regexPattern: "([bBpvV])(\\d+)h(\\d+)",
        dynamicGenerator: (caps) => generateHomMatrix(caps[0], parseInt(caps[1]), parseInt(caps[2])),
    },
    {
        id: "matrix-generic-no-sub",
        trigger: "",
        name: "Generic Matrix (no subscripts)",
        category: "matrix",
        latex: "",
        isRegex: true,
        regexPattern: "([bBpvV])(\\d*)g(\\d*)n",
        dynamicGenerator: (caps) => generateGenericMatrix(caps[0], caps[1] ? parseInt(caps[1]) : 2, false, caps[2] ? parseInt(caps[2]) : 2),
    },
    {
        id: "matrix-generic-sub",
        trigger: "",
        name: "Generic Matrix (with subscripts)",
        category: "matrix",
        latex: "",
        isRegex: true,
        regexPattern: "([bBpvV])(\\d*)g(\\d*)s",
        dynamicGenerator: (caps) => generateGenericMatrix(caps[0], caps[1] ? parseInt(caps[1]) : 2, true, caps[2] ? parseInt(caps[2]) : 2),
    },
    {
        id: "matrix-dotted",
        trigger: "",
        name: "Dotted Matrix",
        category: "matrix",
        latex: "",
        isRegex: true,
        regexPattern: "([bBpvV])gd",
        dynamicGenerator: (caps) => generateDottedMatrix(caps[0]),
    },
    {
        id: "matrix-row-vector",
        trigger: "",
        name: "Row Vector",
        category: "matrix",
        latex: "",
        isRegex: true,
        regexPattern: "([bBpvV])gr",
        dynamicGenerator: (caps) => generateRowVector(caps[0]),
    },
    {
        id: "matrix-col-vector",
        trigger: "",
        name: "Column Vector",
        category: "matrix",
        latex: "",
        isRegex: true,
        regexPattern: "([bBpvV])gc",
        dynamicGenerator: (caps) => generateColVector(caps[0]),
    },
    {
        id: "cases",
        trigger: "cs",
        name: "Cases",
        category: "cases",
        latex: "",
        isRegex: true,
        regexPattern: "(\\d?)cs",
        aliases: ["cases", "piecewise"],
        dynamicGenerator: (caps) => generateCases(caps[0] ? parseInt(caps[0]) : 2),
    },
];

// ============================================================
// OPERATOR TRIGGERS
// ============================================================
const operatorTriggers: TriggerDefinition[] = [
    {
        id: "sq",
        trigger: "sq",
        name: "Square root",
        category: "operator",
        latex: "\\sqrt{#0}",
        choices: ["\\sqrt{#0}", "\\sqrt[#0]{#1}"],
        aliases: ["sqrt", "squareroot", "root"],
    },
    {
        id: "ce",
        trigger: "ce",
        name: "Ceiling",
        category: "operator",
        latex: "\\lceil #0 \\rceil",
        choices: ["\\lceil #0 \\rceil", "\\left\\lceil #0 \\right\\rceil"],
        aliases: ["ceiling", "ceil", "roundup"],
    },
    {
        id: "fl",
        trigger: "fl",
        name: "Floor",
        category: "operator",
        latex: "\\lfloor #0 \\rfloor",
        choices: ["\\lfloor #0 \\rfloor", "\\left\\lfloor #0 \\right\\rfloor"],
        aliases: ["floor", "rounddown"],
    },
    { id: "imp", trigger: "imp", name: "Imaginary part", category: "operator", latex: "\\Im", aliases: ["imaginary", "impart"] },
    { id: "rpa", trigger: "rpa", name: "Real part", category: "operator", latex: "\\Re", aliases: ["realpart"] },
    { id: "mp", trigger: "mp", name: "Minus plus", category: "operator", latex: "\\mp", aliases: ["minusplus"] },
    { id: "pm", trigger: "pm", name: "Plus minus", category: "operator", latex: "\\pm", aliases: ["plusminus"] },
    { id: "tm", trigger: "tm", name: "Times", category: "operator", latex: "\\times", aliases: ["times", "multiply", "cross"] },
    { id: "cd", trigger: "cd", name: "Centered dot", category: "operator", latex: "\\cdot", aliases: ["cdot", "dot"] },
    { id: "cir", trigger: "cir", name: "Circle", category: "operator", latex: "\\circ", aliases: ["circ", "circle", "compose"] },
    { id: "opl", trigger: "opl", name: "Oplus", category: "operator", latex: "\\oplus", aliases: ["oplus", "directsum", "xor"] },
    { id: "omt", trigger: "omt", name: "Otimes", category: "operator", latex: "\\otimes", aliases: ["otimes", "tensor", "tensorproduct"] },
    { id: "dv", trigger: "dv", name: "Mid", category: "operator", latex: "\\mid", aliases: ["divides", "mid"] },
    { id: "ndv", trigger: "ndv", name: "Not mid", category: "operator", latex: "\\nmid", aliases: ["notdivides", "nmid"] },
    { id: "arg", trigger: "arg", name: "Argument", category: "operator", latex: "\\arg", aliases: ["argument"] },
    { id: "deg", trigger: "deg", name: "Degree", category: "operator", latex: "\\deg", aliases: ["degree"] },
    { id: "det", trigger: "det", name: "Determinant", category: "operator", latex: "\\det", aliases: ["determinant"] },
    { id: "dim", trigger: "dim", name: "Dimension", category: "operator", latex: "\\dim", aliases: ["dimension"] },
    { id: "hm", trigger: "hm", name: "Hom", category: "operator", latex: "\\hom", aliases: ["hom", "homomorphism"] },
    { id: "kr", trigger: "kr", name: "Kernel", category: "operator", latex: "\\ker", aliases: ["kernel", "ker"] },
    { id: "lap", trigger: "lap", name: "Laplacian", category: "operator", latex: "\\nabla^2 ", aliases: ["laplacian"] },
    { id: "nb", trigger: "nb", name: "Nabla", category: "operator", latex: "\\nabla", aliases: ["nabla", "gradient", "del"] },
    {
        id: "xm",
        trigger: "xm",
        name: "Maximum",
        category: "operator",
        latex: "\\max",
        choices: ["\\max", "\\max_{#0}"],
        aliases: ["maximum", "max"],
    },
    {
        id: "mu",
        trigger: "mu",
        name: "Minimum",
        category: "operator",
        latex: "\\min",
        choices: ["\\min", "\\min_{#0}"],
        aliases: ["minimum", "min"],
    },
    {
        id: "nf",
        trigger: "nf",
        name: "Infimum",
        category: "operator",
        latex: "\\inf",
        choices: ["\\inf", "\\inf_{#0}"],
        aliases: ["infimum"],
    },
    {
        id: "sr",
        trigger: "sr",
        name: "Supremum",
        category: "operator",
        latex: "\\sup",
        choices: ["\\sup", "\\sup_{#0}"],
        aliases: ["supremum"],
    },
];

// ============================================================
// QUANTUM MECHANICS
// ============================================================
const quantumTriggers: TriggerDefinition[] = [
    { id: "ba", trigger: "ba", name: "Bra", category: "operator", latex: "\\langle #0 \\rvert", aliases: ["bra"] },
    { id: "kt", trigger: "kt", name: "Ket", category: "operator", latex: "\\lvert #0 \\rangle", aliases: ["ket"] },
    { id: "bk", trigger: "bk", name: "Braket", category: "operator", latex: "\\langle #0 | #1 \\rangle", aliases: ["braket", "innerproduct"] },
];

// ============================================================
// DECORATION TRIGGERS
// ============================================================
const decorationTriggers: TriggerDefinition[] = [
    { id: "ovr", trigger: "ovr", name: "Overline", category: "decoration", latex: "\\overline{#0}", aliases: ["overline"] },
    { id: "und", trigger: "und", name: "Underline", category: "decoration", latex: "\\underline{#0}", aliases: ["underline"] },
    { id: "ovb", trigger: "ovb", name: "Overbrace", category: "decoration", latex: "\\overbrace{#0}^{#1}", aliases: ["overbrace"] },
    { id: "unb", trigger: "unb", name: "Underbrace", category: "decoration", latex: "\\underbrace{#0}_{#1}", aliases: ["underbrace"] },
    { id: "bx", trigger: "bx", name: "Boxed", category: "compound", latex: "\\boxed{#0}", aliases: ["boxed", "box"] },
    { id: "abv", trigger: "abv", name: "Overset", category: "compound", latex: "\\overset{#0}{#1}", aliases: ["overset", "above"] },
    { id: "bel", trigger: "bel", name: "Underset", category: "compound", latex: "\\underset{#0}{#1}", aliases: ["underset", "below"] },
];

// ============================================================
// ACCENT TRIGGERS
// ============================================================
const accentTriggers: TriggerDefinition[] = [
    {
        id: "dr",
        trigger: "dr",
        name: "Dot",
        category: "accent",
        latex: "\\dot{#0}",
        choices: ["\\dot{#0}", "\\ddot{#0}", "\\dddot{#0}"],
        aliases: ["dotaccent"],
    },
    {
        id: "ht",
        trigger: "ht",
        name: "Hat",
        category: "accent",
        latex: "\\hat{#0}",
        choices: ["\\hat{#0}", "\\widehat{#0}"],
        aliases: ["hat", "circumflex"],
    },
    { id: "rng", trigger: "rng", name: "Ring", category: "accent", latex: "\\mathring{#0}", aliases: ["ring", "mathring"] },
    {
        id: "til",
        trigger: "til",
        name: "Tilde",
        category: "accent",
        latex: "\\tilde{#0}",
        choices: ["\\tilde{#0}", "\\widetilde{#0}"],
        aliases: ["tilde", "wave"],
    },
    {
        id: "vv",
        trigger: "vv",
        name: "Vector",
        category: "accent",
        latex: "\\vec{#0}",
        aliases: ["vector", "vec"],
    },
];

// ============================================================
// POSTFIX ACCENT TRIGGERS (math mode only)
// Type the letter then the suffix: phat → \hat{p}, zbar → \overline{z}
// ============================================================
const postfixTriggers: TriggerDefinition[] = [
    {
        id: "pf-hat",
        trigger: "<x>hat",
        isRegex: true,
        regexPattern: "([a-zA-Z])hat",
        name: "Hat (postfix)",
        category: "postfix",
        latex: "\\hat{x}",
        dynamicGenerator: (captures) => `\\hat{${captures[0]}}`,
    },
    {
        id: "pf-bar",
        trigger: "<x>bar",
        isRegex: true,
        regexPattern: "([a-zA-Z])bar",
        name: "Bar/Overline (postfix)",
        category: "postfix",
        latex: "\\overline{x}",
        dynamicGenerator: (captures) => `\\overline{${captures[0]}}`,
    },
    {
        id: "pf-vec",
        trigger: "<x>vec",
        isRegex: true,
        regexPattern: "([a-zA-Z])vec",
        name: "Vector (postfix)",
        category: "postfix",
        latex: "\\vec{x}",
        dynamicGenerator: (captures) => `\\vec{${captures[0]}}`,
    },
    {
        id: "pf-til",
        trigger: "<x>til",
        isRegex: true,
        regexPattern: "([a-zA-Z])til",
        name: "Tilde (postfix)",
        category: "postfix",
        latex: "\\tilde{x}",
        dynamicGenerator: (captures) => `\\tilde{${captures[0]}}`,
    },
    {
        id: "pf-dot",
        trigger: "<x>dot",
        isRegex: true,
        regexPattern: "([a-zA-Z])dot",
        name: "Dot (postfix)",
        category: "postfix",
        latex: "\\dot{x}",
        dynamicGenerator: (captures) => `\\dot{${captures[0]}}`,
    },
];

// ============================================================
// LIMIT TRIGGERS
// ============================================================
const limitTriggers: TriggerDefinition[] = [
    {
        id: "lm",
        trigger: "lm",
        name: "Limit",
        category: "limit",
        latex: "\\lim_{#0 \\to #1}",
        choices: ["\\lim_{#0 \\to #1}", "\\lim"],
        aliases: ["limit", "lim"],
    },
    { id: "lif", trigger: "lif", name: "liminf", category: "limit", latex: "\\liminf_{#0 \\to #1}", aliases: ["liminf"] },
    { id: "lsu", trigger: "lsu", name: "limsup", category: "limit", latex: "\\limsup_{#0 \\to #1}", aliases: ["limsup"] },
];

// ============================================================
// SUM & PRODUCT TRIGGERS
// ============================================================
const sumProductTriggers: TriggerDefinition[] = [
    {
        id: "sm",
        trigger: "sm",
        name: "Sum (subscript)",
        category: "sum-product",
        latex: "\\sum_{#0}",
        choices: ["\\sum_{#0}", "\\sum"],
        aliases: ["sum", "summation"],
    },
    { id: "ss", trigger: "ss", name: "Sum (full)", category: "sum-product", latex: "\\sum_{#0}^{#1}", aliases: ["sumfull"] },
    {
        id: "sp-prod",
        trigger: "sp",
        name: "Product (subscript)",
        category: "sum-product",
        latex: "\\prod_{#0}",
        choices: ["\\prod_{#0}", "\\prod"],
        aliases: ["product", "prod"],
    },
    { id: "pp", trigger: "pp", name: "Product (full)", category: "sum-product", latex: "\\prod_{#0}^{#1}", aliases: ["productfull"] },
    { id: "sos", trigger: "sos", name: "Bigoplus (sub)", category: "sum-product", latex: "\\bigoplus_{#0}", aliases: ["bigoplus", "directsumall"] },
    { id: "nos", trigger: "nos", name: "Bigoplus (full)", category: "sum-product", latex: "\\bigoplus_{#0}^{#1}", aliases: ["bigoplusfull"] },
    { id: "sop", trigger: "sop", name: "Bigotimes (sub)", category: "sum-product", latex: "\\bigotimes_{#0}", aliases: ["bigotimes", "tensorall"] },
    { id: "nop", trigger: "nop", name: "Bigotimes (full)", category: "sum-product", latex: "\\bigotimes_{#0}^{#1}", aliases: ["bigotimesfull"] },
];

// ============================================================
// INTEGRAL TRIGGERS
// ============================================================
const integralTriggers: TriggerDefinition[] = [
    { id: "itn", trigger: "itn", name: "Integral", category: "integral", latex: "\\int", aliases: ["integral", "int"] },
    { id: "its", trigger: "its", name: "Integral (sub)", category: "integral", latex: "\\int_{#0}", aliases: ["integralsub"] },
    { id: "itd", trigger: "itd", name: "Integral (full)", category: "integral", latex: "\\int_{#0}^{#1}", aliases: ["integralfull", "definiteintegral"] },
    { id: "itbn", trigger: "itbn", name: "Double integral", category: "integral", latex: "\\iint", aliases: ["doubleintegral", "iint"] },
    { id: "itbs", trigger: "itbs", name: "Double integral (sub)", category: "integral", latex: "\\iint_{#0}", aliases: ["doubleintegralsub"] },
    { id: "ittn", trigger: "ittn", name: "Triple integral", category: "integral", latex: "\\iiint", aliases: ["tripleintegral", "iiint"] },
    { id: "itts", trigger: "itts", name: "Triple integral (sub)", category: "integral", latex: "\\iiint_{#0}", aliases: ["tripleintegralsub"] },
];

// ============================================================
// DERIVATIVE TRIGGERS
// ============================================================
const derivativeTriggers: TriggerDefinition[] = [
    { id: "der", trigger: "der", name: "Derivative", category: "derivative", latex: "\\frac{\\mathrm{d} #0}{\\mathrm{d} #1}", aliases: ["derivative", "ddx"] },
    { id: "ndr", trigger: "ndr", name: "Nth derivative", category: "derivative", latex: "\\frac{\\mathrm{d}^{#0} #1}{\\mathrm{d} #2^{#0}}", aliases: ["nthderivative"] },
    { id: "pdr", trigger: "pdr", name: "Partial derivative", category: "derivative", latex: "\\frac{\\partial #0}{\\partial #1}", aliases: ["partial", "partialderivative"] },
    { id: "npd", trigger: "npd", name: "Nth partial", category: "derivative", latex: "\\frac{\\partial^{#0} #1}{\\partial #2^{#0}}", aliases: ["nthpartial"] },
];

// ============================================================
// FUNCTION TRIGGERS (trig, hyp, inverse, log/exp)
// ============================================================
const functionTriggers: TriggerDefinition[] = [
    { id: "sin", trigger: "sin", name: "sin", category: "function", latex: "\\sin", aliases: ["sine"] },
    { id: "cos", trigger: "cos", name: "cos", category: "function", latex: "\\cos", aliases: ["cosine"] },
    { id: "tan", trigger: "tan", name: "tan", category: "function", latex: "\\tan", aliases: ["tangent"] },
    { id: "cot", trigger: "cot", name: "cot", category: "function", latex: "\\cot", aliases: ["cotangent"] },
    { id: "sec", trigger: "sec", name: "sec", category: "function", latex: "\\sec", aliases: ["secant"] },
    { id: "cc-csc", trigger: "cc", name: "csc", category: "function", latex: "\\csc", aliases: ["cosecant", "csc"] },
    { id: "asin", trigger: "asin", name: "arcsin", category: "function", latex: "\\arcsin", aliases: ["arcsin", "arcsine"] },
    { id: "acos", trigger: "acos", name: "arccos", category: "function", latex: "\\arccos", aliases: ["arccos", "arccosine"] },
    { id: "atan", trigger: "atan", name: "arctan", category: "function", latex: "\\arctan", aliases: ["arctan", "arctangent"] },
    { id: "sinh", trigger: "sih", name: "sinh", category: "function", latex: "\\sinh", aliases: ["sinh", "hypsin"] },
    { id: "cosh", trigger: "csh", name: "cosh", category: "function", latex: "\\cosh", aliases: ["cosh", "hypcos"] },
    { id: "tanh", trigger: "tah", name: "tanh", category: "function", latex: "\\tanh", aliases: ["tanh", "hyptan"] },
    { id: "coth", trigger: "cth", name: "coth", category: "function", latex: "\\coth", aliases: ["coth", "hypcot"] },
    { id: "xp", trigger: "xp", name: "exp", category: "function", latex: "\\exp", aliases: ["exp", "exponential"] },
    { id: "ln", trigger: "ln", name: "ln", category: "function", latex: "\\ln", aliases: ["naturallog"] },
    { id: "lg", trigger: "lg", name: "log", category: "function", latex: "\\log", aliases: ["log", "logarithm"] },
    { id: "fn", trigger: "fn", name: "Function map", category: "function", latex: "#0 : #1 \\longrightarrow #2", aliases: ["function", "mapping"] },
];

// ============================================================
// RELATION TRIGGERS
// ============================================================
const relationTriggers: TriggerDefinition[] = [
    { id: "eq", trigger: "eq", name: "Equiv", category: "relation", latex: "\\equiv", aliases: ["equivalent", "equiv"] },
    { id: "ne", trigger: "ne", name: "Not equal", category: "relation", latex: "\\ne", aliases: ["notequal", "neq"] },
    { id: "nr", trigger: "nr", name: "Not", category: "relation", latex: "\\not", aliases: ["not"] },
    { id: "app", trigger: "app", name: "Approx", category: "relation", latex: "\\approx", aliases: ["approx", "approximately"] },
    {
        id: "cn",
        trigger: "cn",
        name: "Congruent",
        category: "relation",
        latex: "\\cong",
        choices: ["\\cong", "\\ncong"],
        aliases: ["congruent", "cong"],
    },
    { id: "le", trigger: "le", name: "Leq", category: "relation", latex: "\\le", aliases: ["lessthanorequal", "leq"] },
    { id: "ge", trigger: "ge", name: "Geq", category: "relation", latex: "\\ge", aliases: ["greaterthanorequal", "geq"] },
    {
        id: "pc",
        trigger: "pc",
        name: "Precedes",
        category: "relation",
        latex: "\\prec",
        choices: ["\\prec", "\\nprec"],
        aliases: ["precedes", "prec"],
    },
    {
        id: "sx",
        trigger: "sx",
        name: "Succeeds",
        category: "relation",
        latex: "\\succ",
        choices: ["\\succ", "\\nsucc"],
        aliases: ["succeeds", "succ"],
    },
    {
        id: "re",
        trigger: "re",
        name: "Relation (sim)",
        category: "relation",
        latex: "\\sim",
        choices: ["\\sim", "\\nsim"],
        aliases: ["similar", "sim"],
    },
];

// ============================================================
// LOGIC TRIGGERS
// ============================================================
const logicTriggers: TriggerDefinition[] = [
    { id: "fa", trigger: "fa", name: "For all", category: "logic", latex: "\\forall", aliases: ["forall", "universal"] },
    { id: "ex", trigger: "ex", name: "Exists", category: "logic", latex: "\\exists", aliases: ["exists", "thereexists"] },
    { id: "nx", trigger: "nx", name: "Not exists", category: "logic", latex: "\\nexists", aliases: ["notexists", "nexists"] },
    { id: "lt-logic", trigger: "lt", name: "Not", category: "logic", latex: "\\lnot", aliases: ["lnot", "negation"] },
    { id: "lan", trigger: "lan", name: "And", category: "logic", latex: "\\land", aliases: ["land", "logicaland", "conjunction"] },
    { id: "lor", trigger: "lor", name: "Or", category: "logic", latex: "\\lor", aliases: ["logicalor", "disjunction"] },
    { id: "ip", trigger: "ip", name: "Implies", category: "logic", latex: "\\implies", aliases: ["implies", "implication"] },
    { id: "ib", trigger: "ib", name: "Implied by", category: "logic", latex: "\\impliedby", aliases: ["impliedby"] },
    { id: "iff", trigger: "iff", name: "Iff", category: "logic", latex: "\\iff", aliases: ["ifandonlyif", "biconditional"] },
];

// ============================================================
// SET TRIGGERS
// ============================================================
const setTriggers: TriggerDefinition[] = [
    { id: "in", trigger: "in", name: "In", category: "set", latex: "\\in", aliases: ["elementof", "belongsto", "member"] },
    { id: "ntn", trigger: "ntn", name: "Not in", category: "set", latex: "\\notin", aliases: ["notin", "notelement"] },
    { id: "na", trigger: "na", name: "Contains", category: "set", latex: "\\ni", aliases: ["contains", "ni"] },
    { id: "vc", trigger: "vc", name: "Empty set", category: "set", latex: "\\varnothing", aliases: ["emptyset", "empty", "void"] },
    { id: "nun", trigger: "nun", name: "Union", category: "set", latex: "\\cup", aliases: ["union", "cup"] },
    { id: "bun", trigger: "bun", name: "Big union", category: "set", latex: "\\bigcup", aliases: ["bigunion", "bigcup"] },
    { id: "sun", trigger: "sun", name: "Big union (sub)", category: "set", latex: "\\bigcup_{#0}", aliases: ["bigunionsub"] },
    { id: "dun", trigger: "dun", name: "Big union (full)", category: "set", latex: "\\bigcup_{#0}^{#1}", aliases: ["bigunionfull"] },
    { id: "nit", trigger: "nit", name: "Intersection", category: "set", latex: "\\cap", aliases: ["intersection", "cap"] },
    { id: "bit", trigger: "bit", name: "Big intersection", category: "set", latex: "\\bigcap", aliases: ["bigintersection", "bigcap"] },
    { id: "sit", trigger: "sit", name: "Big cap (sub)", category: "set", latex: "\\bigcap_{#0}", aliases: ["bigintersectionsub"] },
    { id: "dit", trigger: "dit", name: "Big cap (full)", category: "set", latex: "\\bigcap_{#0}^{#1}", aliases: ["bigintersectionfull"] },
    { id: "sf-set", trigger: "sf", name: "Set minus", category: "set", latex: "\\setminus", aliases: ["setminus", "without"] },
    { id: "sbs", trigger: "sbs", name: "Subset", category: "set", latex: "\\subset", aliases: ["subset"] },
    { id: "sbq", trigger: "sbq", name: "Subseteq", category: "set", latex: "\\subseteq", aliases: ["subseteq", "subsetequal"] },
    { id: "sus", trigger: "sus", name: "Supset", category: "set", latex: "\\supset", aliases: ["superset", "supset"] },
    { id: "suq", trigger: "suq", name: "Supseteq", category: "set", latex: "\\supseteq", aliases: ["supersetequal", "supseteq"] },
];

// ============================================================
// ARROW TRIGGERS
// ============================================================
const arrowTriggers: TriggerDefinition[] = [
    { id: "rar", trigger: "rar", name: "Right arrow", category: "arrow", latex: "\\longrightarrow", aliases: ["rightarrow", "longrightarrow"] },
    { id: "lar", trigger: "lar", name: "Left arrow", category: "arrow", latex: "\\longleftarrow", aliases: ["leftarrow", "longleftarrow"] },
    { id: "to", trigger: "to", name: "Maps to", category: "arrow", latex: "\\longmapsto", aliases: ["mapsto", "longmapsto"] },
    { id: "lxl", trigger: "lxl", name: "Left ext arrow", category: "arrow", latex: "\\xleftarrow{#0}", aliases: ["xleftarrow"] },
    { id: "lxr", trigger: "lxr", name: "Right ext arrow", category: "arrow", latex: "\\xrightarrow{#0}", aliases: ["xrightarrow"] },
    // Gilles-style short arrow triggers
    { id: "to-short", trigger: "->", name: "To (\u2192)", category: "arrow", latex: "\\to" },
    { id: "mapsto", trigger: "!>", name: "Mapsto (\u21a6)", category: "arrow", latex: "\\mapsto" },
];

// ============================================================
// GREEK LETTER TRIGGERS (. prefix)
// ============================================================
const greekTriggers: TriggerDefinition[] = [
    { id: "g-a", trigger: ".a", name: "alpha", category: "greek", latex: "\\alpha", aliases: ["alpha"] },
    { id: "g-b", trigger: ".b", name: "beta", category: "greek", latex: "\\beta", aliases: ["beta"] },
    { id: "g-c", trigger: ".c", name: "chi", category: "greek", latex: "\\chi", aliases: ["chi"] },
    { id: "g-D", trigger: ".D", name: "Delta", category: "greek", latex: "\\Delta", aliases: ["Delta", "bigdelta"] },
    { id: "g-d", trigger: ".d", name: "delta", category: "greek", latex: "\\delta", aliases: ["delta"] },
    { id: "g-e", trigger: ".e", name: "varepsilon", category: "greek", latex: "\\varepsilon", aliases: ["epsilon", "varepsilon"] },
    { id: "g-G", trigger: ".G", name: "Gamma", category: "greek", latex: "\\Gamma", aliases: ["Gamma", "biggamma"] },
    { id: "g-g", trigger: ".g", name: "gamma", category: "greek", latex: "\\gamma", aliases: ["gamma"] },
    { id: "g-h", trigger: ".h", name: "eta", category: "greek", latex: "\\eta", aliases: ["eta"] },
    { id: "g-i", trigger: ".i", name: "iota", category: "greek", latex: "\\iota", aliases: ["iota"] },
    { id: "g-k", trigger: ".k", name: "kappa", category: "greek", latex: "\\kappa", aliases: ["kappa"] },
    { id: "g-L", trigger: ".L", name: "Lambda", category: "greek", latex: "\\Lambda", aliases: ["Lambda", "biglambda"] },
    { id: "g-l", trigger: ".l", name: "lambda", category: "greek", latex: "\\lambda", aliases: ["lambda"] },
    { id: "g-m", trigger: ".m", name: "mu", category: "greek", latex: "\\mu", aliases: ["mu"] },
    { id: "g-n", trigger: ".n", name: "nu", category: "greek", latex: "\\nu", aliases: ["nu"] },
    { id: "g-O", trigger: ".O", name: "Omega", category: "greek", latex: "\\Omega", aliases: ["Omega", "bigomega"] },
    { id: "g-o", trigger: ".o", name: "omega", category: "greek", latex: "\\omega", aliases: ["omega"] },
    { id: "g-Ph", trigger: ".Ph", name: "Phi", category: "greek", latex: "\\Phi", aliases: ["Phi", "bigphi"] },
    { id: "g-ph", trigger: ".ph", name: "varphi", category: "greek", latex: "\\varphi", aliases: ["phi", "varphi"] },
    { id: "g-Pi", trigger: ".Pi", name: "Pi", category: "greek", latex: "\\Pi", aliases: ["Pi", "bigpi"] },
    { id: "g-pi", trigger: ".pi", name: "pi", category: "greek", latex: "\\pi", aliases: ["pi"] },
    { id: "g-Ps", trigger: ".Ps", name: "Psi", category: "greek", latex: "\\Psi", aliases: ["Psi", "bigpsi"] },
    { id: "g-ps", trigger: ".ps", name: "psi", category: "greek", latex: "\\psi", aliases: ["psi"] },
    { id: "g-r", trigger: ".r", name: "rho", category: "greek", latex: "\\rho", aliases: ["rho"] },
    { id: "g-S", trigger: ".S", name: "Sigma", category: "greek", latex: "\\Sigma", aliases: ["Sigma", "bigsigma"] },
    { id: "g-s", trigger: ".s", name: "sigma", category: "greek", latex: "\\sigma", aliases: ["sigma"] },
    { id: "g-ta", trigger: ".ta", name: "tau", category: "greek", latex: "\\tau", aliases: ["tau"] },
    { id: "g-Th", trigger: ".Th", name: "Theta", category: "greek", latex: "\\Theta", aliases: ["Theta", "bigtheta"] },
    { id: "g-th", trigger: ".th", name: "theta", category: "greek", latex: "\\theta", aliases: ["theta"] },
    { id: "g-U", trigger: ".U", name: "Upsilon", category: "greek", latex: "\\Upsilon", aliases: ["Upsilon", "bigupsilon"] },
    { id: "g-u", trigger: ".u", name: "upsilon", category: "greek", latex: "\\upsilon", aliases: ["upsilon"] },
    { id: "g-X", trigger: ".X", name: "Xi", category: "greek", latex: "\\Xi", aliases: ["Xi", "bigxi"] },
    { id: "g-x", trigger: ".x", name: "xi", category: "greek", latex: "\\xi", aliases: ["xi"] },
    { id: "g-z", trigger: ".z", name: "zeta", category: "greek", latex: "\\zeta", aliases: ["zeta"] },
];

// ============================================================
// SYMBOL TRIGGERS (letter-shaped, misc)
// ============================================================
const symbolTriggers: TriggerDefinition[] = [
    { id: "ha", trigger: "ha", name: "Aleph", category: "symbol", latex: "\\aleph", aliases: ["aleph"] },
    { id: "hb", trigger: "hb", name: "Beth", category: "symbol", latex: "\\beth", aliases: ["beth"] },
    { id: "hd-sym", trigger: "hd", name: "Daleth", category: "symbol", latex: "\\daleth", aliases: ["daleth"] },
    { id: "hg", trigger: "hg", name: "Gimel", category: "symbol", latex: "\\gimel", aliases: ["gimel"] },
    { id: "ll-ell", trigger: "ll", name: "Ell", category: "symbol", latex: "\\ell", aliases: ["ell"] },
    { id: "cm", trigger: "cm", name: "Complement", category: "symbol", latex: "\\complement", aliases: ["complementsym"] },
    { id: "hr", trigger: "hr", name: "Hbar", category: "symbol", latex: "\\hbar", aliases: ["hbar", "planck"] },
    { id: "hl", trigger: "hl", name: "Hslash", category: "symbol", latex: "\\hslash", aliases: ["hslash"] },
    { id: "pt", trigger: "pt", name: "Partial", category: "symbol", latex: "\\partial", aliases: ["partialsym"] },
    { id: "fy", trigger: "fy", name: "Infinity", category: "symbol", latex: "\\infty", aliases: ["infinity", "infty"] },
    { id: "ooo", trigger: "ooo", name: "Infinity (ooo)", category: "symbol", latex: "\\infty" },
    { id: "ang", trigger: "ang", name: "Angle", category: "symbol", latex: "\\angle", aliases: ["angle"] },
    { id: "pr-sym", trigger: "pr", name: "Prime", category: "symbol", latex: "\\prime", aliases: ["prime"] },
];

// ============================================================
// ELLIPSIS & PUNCTUATION TRIGGERS
// ============================================================
const ellipsisTriggers: TriggerDefinition[] = [
    { id: "dd", trigger: "dd", name: "Lower dots", category: "ellipsis", latex: "\\ldots", aliases: ["ldots", "lowerdots", "dots"] },
    { id: "cr-dots", trigger: "cr", name: "Centered dots", category: "ellipsis", latex: "\\cdots", aliases: ["cdots", "centereddots"] },
    { id: "vd", trigger: "vd", name: "Vertical dots", category: "ellipsis", latex: "\\vdots", aliases: ["vdots", "verticaldots"] },
    { id: "gd", trigger: "gd", name: "Diagonal dots", category: "ellipsis", latex: "\\ddots", aliases: ["ddots", "diagonaldots"] },
    { id: "cln", trigger: "cln", name: "Colon", category: "ellipsis", latex: ":", aliases: ["colon"] },
    { id: "def", trigger: "def", name: "Definition :=", category: "ellipsis", latex: ":=", aliases: ["definedas", "colonequals"] },
    { id: "sln", trigger: "sln", name: "Semicolon", category: "ellipsis", latex: ";", aliases: ["semicolon"] },
];

// ============================================================
// SPACING TRIGGERS
// ============================================================
const spacingTriggers: TriggerDefinition[] = [
    { id: "thp", trigger: "thp", name: "Thin space", category: "spacing", latex: "\\,", aliases: ["thinspace"] },
    { id: "mdn", trigger: "mdn", name: "Medium space", category: "spacing", latex: "\\:", aliases: ["mediumspace", "medspace"] },
    { id: "tkp", trigger: "tkp", name: "Thick space", category: "spacing", latex: "\\;", aliases: ["thickspace"] },
    { id: "qu", trigger: "qu", name: "Quad", category: "spacing", latex: "\\quad", aliases: ["quad"] },
    { id: "qq", trigger: "qq", name: "Double quad", category: "spacing", latex: "\\qquad", aliases: ["qquad", "doublequad"] },
    { id: "thn", trigger: "thn", name: "Neg thin", category: "spacing", latex: "\\!", aliases: ["negthinspace", "negspace"] },
    { id: "hs", trigger: "hs", name: "Hspace", category: "spacing", latex: "\\hspace{#0}", aliases: ["hspace"] },
];

// ============================================================
// DISPLAY ENVIRONMENT TRIGGERS
// ============================================================
const displayTriggers: TriggerDefinition[] = [
    { id: "tx", trigger: "tx", name: "Text in math", category: "display", latex: "\\text{#0}", aliases: ["text", "textmode"] },
    { id: "dis", trigger: "dis", name: "Displaystyle", category: "display", latex: "\\displaystyle", aliases: ["displaystyle"] },
    { id: "ty", trigger: "ty", name: "Textstyle", category: "display", latex: "\\textstyle", aliases: ["textstyle"] },
];

// ============================================================
// NUMBERING TRIGGERS
// ============================================================
const numberingTriggers: TriggerDefinition[] = [
    { id: "ntg", trigger: "ntg", name: "No tag", category: "numbering", latex: "\\notag", aliases: ["notag"] },
    { id: "tag", trigger: "tag", name: "Tag", category: "numbering", latex: "\\tag{#0}", aliases: ["tag"] },
];

// ============================================================
// EXPORT ALL
// ============================================================
export const allMathTriggers: TriggerDefinition[] = [
    ...mathFontTriggers,
    ...fractionTriggers,
    ...matrixTriggers,
    ...subSupTriggers,
    ...delimiterTriggers,
    ...operatorTriggers,
    ...quantumTriggers,
    ...decorationTriggers,
    ...accentTriggers,
    ...postfixTriggers,
    ...limitTriggers,
    ...sumProductTriggers,
    ...integralTriggers,
    ...derivativeTriggers,
    ...functionTriggers,
    ...relationTriggers,
    ...logicTriggers,
    ...setTriggers,
    ...arrowTriggers,
    ...greekTriggers,
    ...symbolTriggers,
    ...ellipsisTriggers,
    ...spacingTriggers,
    ...displayTriggers,
    ...numberingTriggers,
];

/**
 * Build MathLive inlineShortcuts map from triggers that produce simple latex
 * (no placeholders, no choices, no regex)
 */
export function buildInlineShortcuts(): Record<string, string> {
    const shortcuts: Record<string, string> = {};
    for (const t of allMathTriggers) {
        // Only simple symbol triggers (no #0 placeholders, no regex)
        if (!t.isRegex && !t.latex.includes("#0") && !t.choices) {
            shortcuts[t.trigger] = t.latex;
        }
    }
    return shortcuts;
}

/**
 * Get structural triggers (those with placeholders that need special insert handling)
 */
export function getStructuralTriggers(): TriggerDefinition[] {
    return allMathTriggers.filter(
        (t) => !t.isRegex && (t.latex.includes("#0") || t.choices)
    );
}

/**
 * Get regex triggers
 */
export function getRegexTriggers(): TriggerDefinition[] {
    return allMathTriggers.filter((t) => t.isRegex);
}
